import React, { useState } from 'react';
import { GameMode, RaceStatus } from '../types';
import { useTheme } from '../../../context/ThemeContext';

interface ModeSelectorProps {
  mode: GameMode;
  setMode: (mode: GameMode) => void;
  onStartRace: () => void;
  onResetRace: () => void;
  raceStatus: RaceStatus;
}

export const ModeSelector: React.FC<ModeSelectorProps> = ({
  mode,
  setMode,
  onStartRace,
  onResetRace,
  raceStatus,
}) => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleModeSelect = (newMode: GameMode) => {
    setMode(newMode);
    setIsOpen(false);
  };

  const handleStartRace = () => {
    onStartRace();
    setIsOpen(false);
  };

  const handleResetRace = () => {
    onResetRace();
    setIsOpen(false);
  };

  return (
    <div className="absolute bottom-4 left-4">
      {/* Menu Panel */}
      {isOpen && (
        <div
          className={`absolute bottom-14 left-0 p-3 rounded-lg shadow-lg border min-w-[160px] ${
            isDarkMode
              ? 'bg-gray-900/95 border-gray-700'
              : 'bg-white/95 border-gray-200'
          }`}
        >
          <div className="space-y-2">
            <div
              className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Game Mode
            </div>

            <button
              onClick={() => handleModeSelect('free')}
              className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                mode === 'free'
                  ? isDarkMode
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : isDarkMode
                  ? 'text-gray-200 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Free Sail
            </button>

            <button
              onClick={() => handleModeSelect('race')}
              className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                mode === 'race'
                  ? isDarkMode
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : isDarkMode
                  ? 'text-gray-200 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Race
            </button>

            {mode === 'race' && (
              <>
                <div
                  className={`border-t my-2 ${
                    isDarkMode ? 'border-gray-700' : 'border-gray-200'
                  }`}
                />

                {raceStatus === 'notStarted' && (
                  <button
                    onClick={handleStartRace}
                    className="w-full text-left px-3 py-2 rounded-md bg-green-500 text-white hover:bg-green-600 transition-colors"
                  >
                    Start Race
                  </button>
                )}

                {(raceStatus === 'racing' || raceStatus === 'finished') && (
                  <button
                    onClick={handleResetRace}
                    className="w-full text-left px-3 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                  >
                    Reset Race
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-3 rounded-lg shadow-lg border transition-all ${
          isDarkMode
            ? 'bg-gray-900/95 border-gray-700 text-gray-200 hover:bg-gray-800'
            : 'bg-white/95 border-gray-200 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {isOpen ? (
            <path d="M18 6L6 18M6 6l12 12" />
          ) : (
            <>
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
};
